// Startsidan (b3 avsnitt 3 B): den gemensamma rapporten högst upp, knapparna "Åka till …" och
// "Resa bort", och ett kort per plats med status, när platsen senast hördes av, nyckelvärdena,
// den senaste händelsen och planerna. Ett klick på kortet öppnar platsens Hälsa.
// b5: "Lägg till plats" för den som är ägare.
// b6: en plats som är ej i drift har ett grått kort. Hörs navet av ändå visar kortet det, och
// ägaren kan ta platsen i drift.
// b11: har navet varit tyst länge på en plats som är ej i drift, är värdena de senast kända.

import { type Oversikt, type Platskort, type Platstyp, sedan } from "@batkoll/gemensam";
import { useState } from "preact/hooks";
import { Nivamarke } from "../komponenter.tsx";

export const TYPNAMN: Record<Platstyp, string> = {
  bat: "Båt",
  hus: "Hus",
  fritidshus: "Fritidshus",
  husbil: "Husbil",
  annat: "Plats",
};

const STATUSTEXT = {
  gron: "Allt är lugnt",
  gul: "Något att se över",
  rod: "Akut",
  gra: "Ej i drift",
} as const;

function Kort(props: { p: Platskort; nu: number }) {
  const { p, nu } = props;
  const h = p.senasteHandelse;
  return (
    <a class={`platskort status-${p.status}`} href={`#plats/${p.id}/halsa`}>
      <div class="platskort-topp">
        {/* Namnet först i länkens namn, för skärmläsare. Statusen kommer efter. */}
        <span class="statusprick" aria-hidden="true" />
        <h2>{p.namn}</h2>
        <span class="dold">{STATUSTEXT[p.status]}</span>
        <span class="platstyp">{TYPNAMN[p.typ]}</span>
      </div>
      {!p.iDrift && <p class="platskort-drift">Ej i drift</p>}
      <p class="platskort-hord">
        {p.navSenastHord === null ? "Har inte hörts av" : `Hördes av ${sedan(p.navSenastHord, nu)}`}
        {p.hordUtanDrift ? ", fast platsen är ej i drift" : ""}
      </p>
      {p.statusText && <p class="platskort-status">{p.statusText}</p>}
      {p.senastKanda != null && (
        <p class="platskort-kanda">Senast kända värden, {sedan(p.senastKanda, nu)}</p>
      )}
      <dl class="nyckelvarden">
        {p.varden.map((v) => (
          <div key={v.namn}>
            <dt>{v.namn}</dt>
            <dd>
              {v.text}
              {v.gammalt && <span class="gammalt"> (gammalt)</span>}
              {v.kalla && <small class="nyckelvarde-kalla">{v.kalla}</small>}
            </dd>
          </div>
        ))}
      </dl>
      {h && (
        <p class="platskort-handelse">
          <Nivamarke niva={h.niva} /> {h.text}
          {h.pagar && h.niva !== "info" ? " · Pågår" : ""}
        </p>
      )}
      {p.styrningslage && <p class="platskort-styrning">Styrning: {p.styrningslage.text}</p>}
      {p.planer.map((plan) => (
        <ul key={plan.id} class="platskort-plan">
          {plan.rader.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      ))}
    </a>
  );
}

/** Kortet, och under det knappen Ta i drift när navet hörs av fast platsen är ej i drift. */
function Platsen(props: { p: Platskort; nu: number; taIDrift?: (id: string) => Promise<void> }) {
  const { p, taIDrift } = props;
  const [tar, setTar] = useState(false);
  if (!p.hordUtanDrift || p.roll !== "agare" || !taIDrift) return <Kort p={p} nu={props.nu} />;
  return (
    <div class="platskort-med-knapp">
      <Kort p={p} nu={props.nu} />
      <button
        type="button"
        class="knapp-huvud"
        disabled={tar}
        onClick={async () => {
          setTar(true);
          try {
            await taIDrift(p.id);
          } finally {
            setTar(false);
          }
        }}
      >
        Ta i drift
      </button>
    </div>
  );
}

export function Startsida(props: {
  o: Oversikt;
  nu: number;
  kanPlanera: boolean;
  /** Kontot är ägare till en plats och kan lägga till fler (b5). */
  kanLaggaTill: boolean;
  /** Tar en plats som är ej i drift i drift (b6). */
  taIDrift?: (id: string) => Promise<void>;
}) {
  const { o, nu } = props;
  if (o.platser.length === 0) {
    return (
      <div class="tomt">
        <h2>Inga platser än</h2>
        <p>En ägare kan bjuda in dig till sin plats. Annars: hör av dig till Jesper.</p>
      </div>
    );
  }
  return (
    <div class="start">
      <section class="rapport" aria-label="Rapport">
        <h2>Rapport</h2>
        <ul>
          {o.rapport.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      </section>
      {props.kanPlanera && (
        <div class="knapprad resknappar">
          <a class="knapp-huvud" href="#aka-till">
            Åka till …
          </a>
          <a class="knapp-andra" href="#resa-bort">
            Resa bort
          </a>
        </div>
      )}
      <div class="platser">
        {o.platser.map((p) => (
          <Platsen key={p.id} p={p} nu={nu} taIDrift={props.taIDrift} />
        ))}
      </div>
      {props.kanLaggaTill && (
        <div class="knapprad">
          <a class="knapp-andra knapplank" href="#ny-plats">
            Lägg till plats
          </a>
        </div>
      )}
    </div>
  );
}
